/**
 * companyDeptSource.js — 系列会社ありグループの「系列会社 × 部署」選択肢ヘルパー
 * ------------------------------------------------------------------
 * 「グループ会社・系列会社構造」PJ Ph.4b で追加。組織レポート 6 画面
 * （PDF30010 / PDF10010Company / PDF70010 / PDF80010 / Excel30010 / Excel30010Compare）・
 * E³ 組織結果（E3OrgResultPage）・シーズン管理者設定（E3DepMgrPage）が共用する。
 *
 * - 系列会社 Select の選択肢（「全社（グループ全体）」= '' を含む）
 * - 系列会社ごとの部署経路（確定組織表 → 無ければユーザーの所属から導出する API へフォールバック）
 * - role 7（シーズン管理者）の所属指定 e3_department を「会社修飾経路」として解釈する
 *   （サーバー utils/role7Scope.js の parseCompanyQualifiedPaths と同一規則。変更時は両方を同期）
 * - シーズン管理者設定用の「系列会社 → 所属」ツリー
 *
 * 系列会社なしグループ（has_affiliates !== true）では各画面はこのヘルパーの系列会社関連機能を使わない
 * （fetchDeptPathsForCompany も affiliate:false では従来の確定組織表／Group.department と同じ結果になる）。
 */
import Axios from 'axios';
import {
  fetchOrgTreeAt,
  orgUnitsToPaths,
  orgUnitsToCompanyNames,
  buildDepartmentTree,
  MAX_DEPT_LEVEL,
} from './orgReportFilter';

// 階層区切り文字（Group.department・User.e3_department と同一）
const PATH_SEP = '／';

// 「全社（グループ全体）」を表す系列会社 Select の値
export const COMPANY_ALL_VALUE = '';
export const COMPANY_ALL_LABEL = '全社（グループ全体）';

// 文字列化して前後空白を除去する（undefined / null は空文字）
const trimText = (value) =>
  value === undefined || value === null ? '' : String(value).trim();

// 経路を正規化する（'／' 分割・各セグメント前後空白除去・空セグメント除去）
const splitSegments = (value) =>
  trimText(value)
    .split(PATH_SEP)
    .map((s) => s.trim())
    .filter((s) => s !== '');

// 名称配列を前後空白除去・空除去・重複除去する（順序は維持）
const uniqueNames = (names) =>
  Array.from(
    new Set((Array.isArray(names) ? names : []).map(trimText).filter((n) => n !== ''))
  );

/**
 * antd `<Select>` の options を作る。
 * @param {string[]} names 系列会社名
 * @param {{ allowAll?: boolean }} [options] allowAll なら先頭に「全社（グループ全体）」（value ''）を置く
 * @returns {Array<{ value: string, label: string }>}
 */
export const companySelectOptions = (names, options = {}) => {
  const list = uniqueNames(names).map((name) => ({ value: name, label: name }));
  if (options.allowAll) {
    return [{ value: COMPANY_ALL_VALUE, label: COMPANY_ALL_LABEL }, ...list];
  }
  return list;
};

/**
 * role 7 の所属指定（e3_department・最大 50 件）を「会社修飾経路」として解釈する。
 * 'A社' = その系列会社全体 / 'A社／本社／企画部' = その系列会社内の経路（配下を含む）。
 * 先頭セグメント = 系列会社名（区分の解釈はしない）。
 *
 * @param {Array|Object} e3Department User.e3_department（配列、または index キーのオブジェクト）
 * @returns {{ companies: Set<string>|null, scopes: Array<{company: string, path: string}>|null }}
 *   全て空欄なら { companies: null, scopes: null }（制限なし）。scopes は重複除去済み・path '' は会社全体
 */
export const parseCompanyQualifiedPaths = (e3Department) => {
  const values = Array.isArray(e3Department)
    ? e3Department
    : e3Department && typeof e3Department === 'object'
    ? Object.values(e3Department)
    : [];
  const companies = new Set();
  const scopes = [];
  const seen = new Set();
  values.forEach((value) => {
    const segments = splitSegments(value);
    if (segments.length === 0) return;
    const company = segments[0];
    const path = segments.slice(1).join(PATH_SEP);
    companies.add(company);
    const key = `${company}${PATH_SEP}${path}`;
    if (seen.has(key)) return;
    seen.add(key);
    scopes.push({ company, path });
  });
  if (companies.size === 0) return { companies: null, scopes: null };
  return { companies, scopes };
};

/**
 * 系列会社 Select の選択肢を、閲覧可能な系列会社（role 7 の companies）で絞り込む。
 * @param {string[]} names 系列会社名（会社名ルール）
 * @param {Set<string>|null} allowedCompanies null = 制限なし（全社も選べる）
 * @returns {{ names: string[], allowAll: boolean }}
 *   制限ありで会社名ルールと 1 件も一致しない場合は、割り当てられた会社名をそのまま選択肢にする
 */
export const companyChoices = (names, allowedCompanies = null) => {
  const list = uniqueNames(names);
  if (!allowedCompanies) return { names: list, allowAll: true };
  let restricted = list.filter((name) => allowedCompanies.has(name));
  if (restricted.length === 0) restricted = uniqueNames(Array.from(allowedCompanies));
  return { names: restricted, allowAll: false };
};

/**
 * 現在の選択値が選択肢に無ければ既定値（全社が選べれば ''、選べなければ先頭の会社）へ補正する。
 * @param {string} current 現在の選択値
 * @param {{ names: string[], allowAll: boolean }} choices companyChoices の戻り値
 * @returns {string}
 */
export const normalizeCompanySelection = (current, choices) => {
  const names = (choices && choices.names) || [];
  const allowAll = !!(choices && choices.allowAll);
  const fallback = allowAll ? COMPANY_ALL_VALUE : names[0] || COMPANY_ALL_VALUE;
  const value = trimText(current);
  if (value === '') return fallback;
  return names.includes(value) ? value : fallback;
};

/**
 * 指定系列会社について、role 7 の部署範囲（系列会社内の経路）を返す。
 * @param {{ scopes: Array|null }} parsed parseCompanyQualifiedPaths の戻り値
 * @param {string} company 系列会社名（'' = 全社）
 * @returns {string[]|null} null = 制限なし（割り当て無し・会社全体の割り当てあり・全社）
 */
export const companyScopePaths = (parsed, company) => {
  if (!parsed || !Array.isArray(parsed.scopes)) return null;
  const name = trimText(company);
  if (name === '') return null;
  const paths = parsed.scopes.filter((s) => s.company === name).map((s) => s.path);
  if (paths.includes('')) return null;
  return paths;
};

/**
 * 部署経路のうち、範囲（scopePaths）の経路そのもの・配下（前方一致）にあるものだけを返す。
 * @param {string[]} paths 部署経路
 * @param {string[]|null} scopePaths null = 制限なし（paths をそのまま返す）
 * @returns {string[]}
 */
export const pathsWithinScopes = (paths, scopePaths) => {
  const list = Array.isArray(paths) ? paths : [];
  if (!Array.isArray(scopePaths)) return list.slice();
  return list.filter((p) =>
    scopePaths.some((s) => s !== '' && (p === s || p.startsWith(s + PATH_SEP)))
  );
};

/**
 * シーズン管理者設定（E3DepMgrPage）用の「系列会社 → 所属」ツリーを作る。
 * ルート = 系列会社（value = 会社名）、子 = 'A社／本社／…' のように会社名を前置した経路。
 * 会社ノード自体も選択可能（= その系列会社全体）。
 *
 * @param {string[]} companies 系列会社名
 * @param {Object<string, string[]>|Map} pathsByCompany 会社名 → 会社内の部署経路
 * @param {{ icon?: any, companyIcon?: any }} [options] icon = 部署ノード、companyIcon = 会社ノードのアイコン
 * @returns {Array} antd TreeSelect の treeData
 */
export const buildCompanyDeptTree = (companies, pathsByCompany, options = {}) => {
  const names = uniqueNames(companies);
  const getPaths = (name) => {
    if (!pathsByCompany) return [];
    const v =
      pathsByCompany instanceof Map ? pathsByCompany.get(name) : pathsByCompany[name];
    return Array.isArray(v) ? v : [];
  };
  const all = [];
  names.forEach((name) => {
    all.push(name);
    getPaths(name).forEach((p) => {
      const segments = splitSegments(p);
      if (segments.length > 0) all.push([name, ...segments].join(PATH_SEP));
    });
  });
  const treeOptions = { maxLevel: MAX_DEPT_LEVEL + 1 };
  if (options.icon !== undefined) treeOptions.icon = options.icon;
  const roots = buildDepartmentTree(all, treeOptions);
  if (options.companyIcon !== undefined) {
    roots.forEach((root) => {
      root.icon = options.companyIcon;
    });
  }
  return roots;
};

/**
 * ユーザーの所属から導出した部署経路を取得する（確定組織表が無いグループのフォールバック）。
 * GET /api/users/dept-paths/:groupCode?company=&date=
 * @returns {Promise<string[]|null>} 取得失敗時は null
 */
const fetchUserDeptPaths = async (groupCode, company, dateStr) => {
  try {
    const params = {};
    const name = trimText(company);
    if (name !== '') params.company = name;
    if (dateStr) params.date = dateStr;
    const res = await Axios.get(
      `/api/users/dept-paths/${encodeURIComponent(groupCode)}`,
      { params }
    );
    if (res && res.data && res.data.success && Array.isArray(res.data.paths)) {
      return res.data.paths;
    }
    return null;
  } catch (error) {
    console.log('所属一覧（ユーザー由来）の取得に失敗しました', error);
    return null;
  }
};

// 確定組織表の units から、指定系列会社の部署経路を作る
const pathsFromUnits = (units, company, affiliate) => {
  const name = trimText(company);
  if (affiliate && orgUnitsToCompanyNames(units).length > 0) {
    // 系列会社ノード（level 0）のある組織表: 会社指定ならその会社の配下、'' なら全系列会社の和集合
    return orgUnitsToPaths(units, name !== '' ? { company: name } : {});
  }
  // 系列会社ノードの無い組織表: 従来どおり全体
  return orgUnitsToPaths(units);
};

// 系列会社ありグループで「系列会社別の経路」を組織表から取れるか
// （= 系列会社ノード（level 0）のある確定組織表。無い組織表は会社を区別できないため使わない。
//   サーバー routes/result30010.js の「rule.source==='org' のときだけ組織表」と同じ規則）
const hasCompanyNodes = (units) => orgUnitsToCompanyNames(units).length > 0;

/**
 * 指定系列会社の部署経路を取得する。
 * ① 基準日に有効な確定組織表があれば、その系列会社（level 0）配下の経路（source: 'org'）。
 *    系列会社なしグループでは系列会社ノードの無い組織表を従来どおり全体で返す（source: 'org'）。
 * ② 系列会社ありグループ（options.affiliate）で確定組織表が無い、または組織表に系列会社ノード（level 0）が
 *    無く会社を区別できないときは、ユーザーの所属から導出した経路（source: 'users'。サーバーの E³ Web 結果と同じ規則）。
 * ③ それ以外（系列会社なしグループで組織表なし・取得失敗）は null
 *    （呼び出し側は従来の Group.department へフォールバックする）。
 *
 * @param {string} groupCode グループコード
 * @param {string} company 系列会社名（'' = 全社）
 * @param {string} dateStr 基準日 'YYYY-MM-DD'
 * @param {{ affiliate?: boolean }} [options] 系列会社ありグループか（Group.has_affiliates === true）
 * @returns {Promise<{ paths: string[], source: 'org'|'users' }|null>}
 */
export const fetchDeptPathsForCompany = async (groupCode, company, dateStr, options = {}) => {
  try {
    if (!groupCode) return null;
    const affiliate = options.affiliate === true;
    const { version, units } = await fetchOrgTreeAt(groupCode, dateStr);
    const hasOrg = !!version && Array.isArray(units) && units.length > 0;
    // 系列会社ありグループでは系列会社ノードの無い組織表を使わない（ユーザー由来へ）
    if (hasOrg && (!affiliate || hasCompanyNodes(units))) {
      return { paths: pathsFromUnits(units, company, affiliate), source: 'org' };
    }
    if (!affiliate) return null;
    const paths = await fetchUserDeptPaths(groupCode, company, dateStr);
    return paths ? { paths, source: 'users' } : null;
  } catch (error) {
    console.log('部署一覧の取得に失敗しました', error);
    return null;
  }
};

/**
 * 複数の系列会社について部署経路をまとめて取得する（確定組織表の取得は 1 回）。
 * シーズン管理者設定（E3DepMgrPage）の「系列会社 → 所属」ツリー用。
 * @param {string} groupCode グループコード
 * @param {string[]} companies 系列会社名
 * @param {string} dateStr 基準日 'YYYY-MM-DD'
 * @returns {Promise<Object<string, string[]>>} 会社名 → 部署経路（取得失敗の会社は []）
 */
export const fetchDeptPathsByCompany = async (groupCode, companies, dateStr) => {
  const names = uniqueNames(companies);
  const out = {};
  names.forEach((name) => {
    out[name] = [];
  });
  if (!groupCode || names.length === 0) return out;
  try {
    const { version, units } = await fetchOrgTreeAt(groupCode, dateStr);
    // 系列会社ノード（level 0）のある確定組織表だけを使う（無ければ会社ごとにユーザー由来）
    if (version && Array.isArray(units) && units.length > 0 && hasCompanyNodes(units)) {
      names.forEach((name) => {
        out[name] = pathsFromUnits(units, name, true);
      });
      return out;
    }
    const results = await Promise.all(
      names.map((name) => fetchUserDeptPaths(groupCode, name, dateStr))
    );
    names.forEach((name, i) => {
      out[name] = results[i] || [];
    });
    return out;
  } catch (error) {
    console.log('系列会社別の部署一覧の取得に失敗しました', error);
    return out;
  }
};

/**
 * Date（または日付文字列）→ 'YYYY-MM-DD'（ローカル時刻）。未指定・不正値は今日。
 */
export const toYmdDash = (value) => {
  const d = value ? new Date(value) : new Date();
  const date = Number.isNaN(d.getTime()) ? new Date() : d;
  return (
    date.getFullYear() +
    '-' +
    ('0' + (date.getMonth() + 1)).slice(-2) +
    '-' +
    ('0' + date.getDate()).slice(-2)
  );
};
